import { useEffect, useRef } from "react";

/*
 * Scroll-reveal for homepage sections.
 *
 * Prerender-safe: content is visible by default. Only when JS runs in a real
 * browser (not puppeteer — navigator.webdriver) are elements below the fold
 * hidden and then revealed once via IntersectionObserver, so the static
 * snapshot in build/ always contains fully visible markup for crawlers.
 *
 * Usage: const ref = useReveal(); <div ref={ref} className="home-reveal">…</div>
 */
function useReveal() {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (typeof window === "undefined" || navigator.webdriver) return undefined;
    if (!("IntersectionObserver" in window)) return undefined;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return undefined;

    const rect = el.getBoundingClientRect();
    if (rect.top < window.innerHeight * 0.9) return undefined; // already in view

    el.classList.add("home-reveal--pending");

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            el.classList.remove("home-reveal--pending");
            el.classList.add("home-reveal--in");
            observer.disconnect();
          }
        });
      },
      { rootMargin: "0px 0px -40px 0px", threshold: 0.05 }
    );
    observer.observe(el);

    return () => observer.disconnect();
  }, []);

  return ref;
}

export default useReveal;
